import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TrialSubscriptionService } from '../trial-subscription.service';
import { AlertComponent } from '../../alert/alert.component';
import { DataTableDirective } from 'angular-datatables';
import { Subject } from 'rxjs';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

declare var $: any;

@Component({
  selector: 'app-trial-user-list-from-dc-website',
  templateUrl: './trial-user-list-from-dc-website.component.html',
  styleUrls: ['./trial-user-list-from-dc-website.component.css']
})
export class TrialUserListFromDcWebsiteComponent implements OnInit {
  @ViewChild(DataTableDirective, { static: false })
  dtElement: DataTableDirective;
  
  dtOptions: DataTables.Settings = {};
  dtTrigger: Subject<any> = new Subject();
  
  trialUsersList: any[] = [];
  isLoading: boolean = false;
  errorMessage: string = '';
  
  // Edit user properties
  editingUser: any = null;
  originalExtendedDays: number = 0; // Store original days from backend
  extendTrialForm: any = {
    IsExtended: 'no', // 'yes' or 'no' for radio button
    additionalDays: 0  // Days to ADD to existing days
  };

  constructor(
    private trialSubscriptionService: TrialSubscriptionService,
    private router: Router,
    private alert: AlertComponent
  ) { }

  ngOnInit(): void {
    this.dtOptions = {
      destroy: true,
      pagingType: 'full_numbers',
      paging: true,
      pageLength: 10,
      scrollX: true,
      scrollCollapse: true,
      stateSave: true,
      stateDuration: -1,
      search: true,
      order: [[0, 'desc']], // Sort by first column (Sr. No.) in descending order
      language: {
        paginate: {
          first: '<<',
          last: '>>',
          next: '>',
          previous: '<',
        },
        search: 'Search:',
        lengthMenu: 'Show _MENU_ entries',
        info: 'Showing _START_ to _END_ of _TOTAL_ entries',
        infoEmpty: 'No entries available',
        infoFiltered: '(filtered from _MAX_ total entries)',
        zeroRecords: 'No matching records found'
      }
    };

    this.loadTrialUsers();
  }

  loadTrialUsers() {
    this.isLoading = true;
    this.errorMessage = '';
    
    this.trialSubscriptionService.getTrialUsersList()
      .then((response: any) => {
        console.log('Trial Users Response:', response);
        this.isLoading = false;
        
        if (response && response.success) {
          // Get data from the response
          this.trialUsersList = response.data || [];
          
          // Trigger DataTable rendering after data is loaded
          setTimeout(() => {
            this.dtTrigger.next();
          }, 100);
        } else {
          this.errorMessage = response.message || 'Failed to load trial users.';
          this.alert.error1(this.errorMessage);
        }
      })
      .catch((error: any) => {
        console.error('Error loading trial users:', error);
        this.isLoading = false;
        
        let errorMsg = 'Failed to load trial users. ';
        if (error.status === 0) {
          errorMsg += 'Unable to connect to server.';
        } else if (error.status === 404) {
          errorMsg += 'API endpoint not found.';
        } else if (error.status === 500) {
          errorMsg += 'Server error occurred.';
        } else {
          errorMsg += error.message || 'Unknown error.';
        }
        
        this.errorMessage = errorMsg;
        this.alert.error1(errorMsg);
      });
  }

  refreshUsersList() {
    // Destroy existing DataTable
    if (this.dtElement && this.dtElement.dtInstance) {
      this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
        dtInstance.destroy();
        
        // Create new Subject for DataTable trigger
        this.dtTrigger = new Subject();
        
        this.loadTrialUsers();
      });
    } else {
      // Create new Subject for DataTable trigger
      this.dtTrigger = new Subject();
      
      this.loadTrialUsers();
    }
  }

  // Open Extend Trial modal
  openEditModal(user: any) {
    // Clean up any lingering backdrops first
    $('.modal-backdrop').remove();
    $('body').removeClass('modal-open');
    
    // Clone the user object
    this.editingUser = { ...user };
    
    // Store original days from backend
    this.originalExtendedDays = user.ExtendedUntil || 0;
    
    // Set form values
    this.extendTrialForm = {
      IsExtended: user.IsExtended ? 'yes' : 'no',
      additionalDays: 0  // Reset to 0 for new input
    };
    
    // Ensure modal element exists and show it
    const modalElement = $('#editUserModal');
    
    // If modal is currently visible, hide it first then show again
    if (modalElement.hasClass('show')) {
      modalElement.one('hidden.bs.modal', () => {
        // Clean up after hiding
        $('.modal-backdrop').remove();
        $('body').removeClass('modal-open');
        
        // Wait a bit and show again
        setTimeout(() => {
          modalElement.modal('show');
        }, 100);
      });
      modalElement.modal('hide');
    } else {
      // Modal is not shown, just open it
      modalElement.modal('show');
    }
  }

  // Update user - only IsExtended and ExtendedUntil fields
  updateTrialUser() {
    if (!this.editingUser || !this.editingUser._id) {
      this.alert.error1('No user selected for update');
      return;
    }

    // Validate
    if (this.extendTrialForm.IsExtended === 'yes') {
      if (!this.extendTrialForm.additionalDays || this.extendTrialForm.additionalDays <= 0) {
        this.alert.error1('Please enter a valid number of days to extend');
        return;
      }
    }

    this.isLoading = true;
    
    // Calculate new total days (additive)
    let newTotalDays = this.originalExtendedDays;
    if (this.extendTrialForm.IsExtended === 'yes') {
      newTotalDays = this.originalExtendedDays + parseInt(this.extendTrialForm.additionalDays);
    }
    
    // Prepare update data - pass ALL original fields + updated IsExtended and ExtendedUntil
    const updateData = {
      first_name: this.editingUser.first_name,
      last_name: this.editingUser.last_name,
      email: this.editingUser.email,
      phone: this.editingUser.phone,
      company_details: this.editingUser.company_details,
      status: this.editingUser.status,
      source: this.editingUser.source,
      notes: this.editingUser.notes,
      IsExtended: this.extendTrialForm.IsExtended === 'yes',
      ExtendedUntil: newTotalDays
    };
    
    this.trialSubscriptionService.updateTrialUser(this.editingUser._id, updateData)
      .then((response: any) => {
        this.isLoading = false;
        
        if (response && response.success) {
          // First, close the modal properly
          this.closeModalAndRefresh();
        } else {
          this.alert.error1(response.message || 'Failed to update trial period.');
        }
      })
      .catch((error: any) => {
        console.error('Error updating trial period:', error);
        this.isLoading = false;
        
        let errorMsg = 'Failed to update trial period. ';
        if (error.status === 0) {
          errorMsg += 'Unable to connect to server. Check CORS settings.';
        } else if (error.status === 404) {
          errorMsg += 'User not found.';
        } else if (error.status === 500) {
          errorMsg += 'Server error occurred.';
        } else {
          errorMsg += error.message || 'Unknown error.';
        }
        
        this.alert.error1(errorMsg);
      });
  }

  // Close modal and refresh list with proper timing
  closeModalAndRefresh() {
    // Hide modal first
    const modalElement = $('#editUserModal');
    
    // Remove all event listeners to prevent conflicts
    modalElement.off('hidden.bs.modal');
    
    // Set up one-time event listener for when modal is fully hidden
    modalElement.one('hidden.bs.modal', () => {
      // Clean up any lingering backdrops
      $('.modal-backdrop').remove();
      $('body').removeClass('modal-open');
      
      // Show success message after modal is fully closed
      this.alert.success('Trial period updated successfully!');
      
      // Wait a bit before refreshing the table to avoid conflicts
      setTimeout(() => {
        this.refreshUsersList();
      }, 200);
    });
    
    // Now hide the modal
    modalElement.modal('hide');
  }

  // Cancel edit
  cancelEdit() {
    const modalElement = $('#editUserModal');
    
    // Remove any existing event listeners
    modalElement.off('hidden.bs.modal');
    
    // Set up one-time cleanup event
    modalElement.one('hidden.bs.modal', () => {
      $('.modal-backdrop').remove();
      $('body').removeClass('modal-open');
      
      // Reset form data after modal is fully closed
      this.editingUser = null;
      this.originalExtendedDays = 0;
      this.extendTrialForm = {
        IsExtended: 'no',
        additionalDays: 0
      };
    });
    
    // Hide the modal
    modalElement.modal('hide');
  }

  confirmDelete(user: any) {
    console.log('Delete user:', user);
    
    // Show confirmation dialog
    const confirmMessage = `Are you sure you want to delete the trial user:\n\n${user.first_name} ${user.last_name}\n${user.email}?`;
    
    if (confirm(confirmMessage)) {
      this.deleteTrialUser(user._id);
    }
  }

  deleteTrialUser(userId: string) {
    this.isLoading = true;
    
    this.trialSubscriptionService.deleteTrialUser(userId)
      .then((response: any) => {
        console.log('Delete response:', response);
        this.isLoading = false;
        
        if (response && response.success) {
          this.alert.success('Trial user deleted successfully!');
          
          // Refresh the list after successful deletion
          this.refreshUsersList();
        } else {
          this.alert.error1(response.message || 'Failed to delete trial user.');
        }
      })
      .catch((error: any) => {
        console.error('Error deleting trial user:', error);
        this.isLoading = false;
        
        let errorMsg = 'Failed to delete trial user. ';
        if (error.status === 0) {
          errorMsg += 'Unable to connect to server.';
        } else if (error.status === 404) {
          errorMsg += 'User not found.';
        } else if (error.status === 500) {
          errorMsg += 'Server error occurred.';
        } else {
          errorMsg += error.message || 'Unknown error.';
        }
        
        this.alert.error1(errorMsg);
      });
  }

  navigateToCreateUser() {
    this.router.navigate(['/create-trial-user']);
  }

  navigateToLogin() {
    this.router.navigate(['/login']);
  }

  // Export to Excel
  exportToExcel() {
    if (this.trialUsersList.length === 0) {
      this.alert.error1('No data available to export.');
      return;
    }

    // Prepare data for export
    const exportData = this.trialUsersList.map((user, index) => ({
      'Sr. No.': index + 1,
      'First Name': user.first_name || '',
      'Last Name': user.last_name || '',
      'Email': user.email || '',
      'Phone': user.phone || '',
      'Company Details': user.company_details || '',
      'Status': user.status || '',
      'Source': user.source || '',
      'Notes': user.notes || '',
      'Created At': user.created_at ? new Date(user.created_at).toLocaleString() : '',
      'Updated At': user.updated_at ? new Date(user.updated_at).toLocaleString() : ''
    }));

    // Create worksheet
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);

    // Set column widths
    const columnWidths = [
      { wch: 8 },  // Sr. No.
      { wch: 15 }, // First Name
      { wch: 15 }, // Last Name
      { wch: 25 }, // Email
      { wch: 15 }, // Phone
      { wch: 30 }, // Company Details
      { wch: 12 }, // Status
      { wch: 20 }, // Source
      { wch: 30 }, // Notes
      { wch: 20 }, // Created At
      { wch: 20 }  // Updated At
    ];
    ws['!cols'] = columnWidths;

    // Create workbook and add the worksheet
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Trial Users');

    // Generate file name with current date
    const fileName = `Trial_Users_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Save to file
    XLSX.writeFile(wb, fileName);

    this.alert.success('Data exported to Excel successfully!');
  }

  // Export to PDF
  exportToPDF() {
    if (this.trialUsersList.length === 0) {
      this.alert.error1('No data available to export.');
      return;
    }

    const doc = new jsPDF('landscape');

    // Add title
    doc.setFontSize(18);
    doc.text('Trial Users List', 14, 22);

    // Add date
    doc.setFontSize(11);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 30);

    // Prepare table data
    const tableData = this.trialUsersList.map((user, index) => [
      index + 1,
      user.first_name || '',
      user.last_name || '',
      user.email || '',
      user.phone || '',
      user.company_details || '',
      user.status || '',
      user.source || '',
      user.created_at ? new Date(user.created_at).toLocaleDateString() : ''
    ]);

    // Add table
    (doc as any).autoTable({
      head: [['Sr.', 'First Name', 'Last Name', 'Email', 'Phone', 'Company', 'Status', 'Source', 'Created']],
      body: tableData,
      startY: 35,
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 2
      },
      headStyles: {
        fillColor: [0, 123, 255],
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      margin: { top: 35 }
    });

    // Generate file name with current date
    const fileName = `Trial_Users_${new Date().toISOString().split('T')[0]}.pdf`;

    // Save PDF
    doc.save(fileName);

    this.alert.success('Data exported to PDF successfully!');
  }

  ngOnDestroy(): void {
    // Unsubscribe from DataTable trigger
    this.dtTrigger.unsubscribe();
  }
}


